import type {
  RoleAssignment,
  RoleWithPermissions,
} from '@obraplan/core/domain/access/permission-resolver';
import type { ApiKeyId } from '@obraplan/core/domain/access/api-key';
import type { AuthSessionId } from '@obraplan/core/domain/access/authentication';
import type { ProjectId, RoleId, UserId } from '@obraplan/core/domain/shared/ids';

/**
 * Quem está operando o sistema agora.
 *
 * Toda escrita registra este usuário na auditoria, e é isso que responde "quem
 * alterou?" (Q13, RN-130).
 *
 * No uso local era o perfil selecionado numa lista, e o ADR-102 dizia com todas as
 * letras que isso era controle operacional e não fronteira de segurança. Com o servidor
 * ela passa a nascer de um token de sessão (Fase 12) — o mesmo objeto, produzido de
 * outro jeito, que é o motivo de nenhum caso de uso ter mudado por causa da virada.
 */
export interface Session {
  readonly userId: UserId;
  readonly userName: string;
  /**
   * O e-mail de quem opera.
   *
   * É por ele que o sistema de compras identifica o requisitante — lá o solicitante é um
   * usuário cadastrado, não um nome em texto, e o e-mail é a única chave que os dois
   * sistemas compartilham sem tabela de-para.
   *
   * Ausente nos perfis que vieram do uso local, criados quando entrar era escolher um
   * nome numa lista.
   */
  readonly userEmail?: string;
  readonly assignments: readonly RoleAssignment[];
  readonly roles: ReadonlyMap<RoleId, RoleWithPermissions>;
  /** Obra em contexto. Ausente em operações de sistema (perfis, backup). */
  readonly projectId?: ProjectId;
  /**
   * A sessão do aparelho que produziu este sujeito.
   *
   * Ausente no uso local, onde não há token. É o que a saída explícita revoga — sem
   * isto, "sair" no tablet do canteiro derrubaria também o computador do escritório.
   */
  readonly authSessionId?: AuthSessionId;
  /**
   * A chave de API que produziu este sujeito (07/10/2026).
   *
   * Presente só quando quem opera é um agente, e não uma pessoa. O `userId` é o de quem
   * criou a chave — a pessoa que responde por ela —, e o `userName` é o nome da chave. O
   * pipeline da API é quem limita o que ela alcança; aqui fica só a identidade.
   */
  readonly apiKeyId?: ApiKeyId;
}

export function withProject(session: Session, projectId: ProjectId | undefined): Session {
  return projectId === undefined
    ? {
        userId: session.userId,
        userName: session.userName,
        assignments: session.assignments,
        roles: session.roles,
        ...(session.userEmail === undefined ? {} : { userEmail: session.userEmail }),
        // Reconstruir o objeto sem a obra não pode perder a sessão do aparelho junto:
        // seria uma saída que não revoga nada, silenciosamente.
        ...(session.authSessionId === undefined ? {} : { authSessionId: session.authSessionId }),
        ...(session.apiKeyId === undefined ? {} : { apiKeyId: session.apiKeyId }),
      }
    : { ...session, projectId };
}
